import { HttpEvent, HttpEventCallbacks, httpGet } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";

export type OwnershipModel =
  | "shares"
  | "membership_interests"
  | "partnership_interests"
  | "units"
  | "none";

export const registerSubscriptions = [
  "ownership_register",
  "partnership_register",
  "foundation",
] as const;

export type RegisterSubscription = (typeof registerSubscriptions)[number];

type NormalizeOp = "strip_spaces" | "strip_punctuation" | "uppercase" | "lowercase" | "zero_pad";

export type Localized = Record<Language, string>;

export type EntityType = {
  rank: number;
  code: string;
  label: Localized;
  category: string;
  ownershipModel: OwnershipModel;
  subscription: RegisterSubscription | null;
  capTableRelevant: boolean;
  isDefault: boolean;
  onboarding: { allowed: boolean; message?: Localized };
};

export type CompanyIdentifier = {
  label: Localized;
  scope: "national" | "subdivision";
  regex: string;
  normalize: NormalizeOp[];
  mask?: string;
  exampleFormatted: string;
  minLength: number;
  maxLength: number;
  notes?: string;
  isFormatAdvisory: boolean;
};

export type SubdivisionNumber = { regex: string; example: string };

export type Subdivision = { code: string; label: Localized; number?: SubdivisionNumber };

export type RegistryCountry = {
  iso2: string;
  name: Localized;
  verification: { supported: boolean; entityTypeFromRegister?: boolean };
  identifiers: CompanyIdentifier[];
  entityTypes: EntityType[];
  reviewNote?: string;
  subdivisionLabel?: Localized;
  subdivisions: Subdivision[];
};

export type CountryOption = { iso2: string; name: Localized };

export type CompanyLookupResponse = {
  name?: string;
  subdivision?: string;
  entityTypeCode?: string;
};

export type CompanyRegistryApiClient = {
  getCountries: (params: HttpEventCallbacks<CountryOption[]>) => HttpEvent<CountryOption[]>;
  getCountry: (
    iso2: string,
    params: HttpEventCallbacks<RegistryCountry>,
  ) => HttpEvent<RegistryCountry>;
  lookupCompany: (
    iso2: string,
    identifier: string,
    params: HttpEventCallbacks<CompanyLookupResponse>,
  ) => HttpEvent<CompanyLookupResponse>;
  searchCompanies: (
    iso2: string,
    name: string,
    params: HttpEventCallbacks<CompanySearchHit[]>,
  ) => HttpEvent<CompanySearchHit[]>;
};

export type CompanySearchHit = { name: string; identifier: string };

export const companyRegistryApiClient: CompanyRegistryApiClient = {
  getCountries: (params) => httpGet({ url: "/company-registry/countries", ...params }),
  getCountry: (iso2, params) =>
    httpGet({ url: `/company-registry/${encodeURIComponent(iso2)}`, ...params }),
  lookupCompany: (iso2, identifier, params) =>
    httpGet({
      url: `/company-registry/${encodeURIComponent(iso2)}/lookup/${encodeURIComponent(identifier)}`,
      ...params,
    }),
  searchCompanies: (iso2, name, params) =>
    httpGet({
      url: `/company-registry/${encodeURIComponent(iso2)}/search?name=${encodeURIComponent(name)}`,
      ...params,
    }),
};

export function primaryIdentifier(country: RegistryCountry | undefined) {
  return country?.identifiers[0];
}

export function identifierFor(
  country: RegistryCountry | undefined,
  subdivision: string | null,
): CompanyIdentifier | undefined {
  const base = primaryIdentifier(country);
  const number = country?.subdivisions.find((option) => option.code === subdivision)?.number;
  return base && number ? { ...base, regex: number.regex, exampleFormatted: number.example } : base;
}

export function normalizeIdentifier(value: string, rule: CompanyIdentifier): string {
  return rule.normalize.reduce((acc, op) => {
    switch (op) {
      case "strip_spaces":
        return acc.replace(/\s/g, "");
      case "strip_punctuation":
        return acc.replace(/[^0-9A-Za-z]/g, "");
      case "uppercase":
        return acc.toUpperCase();
      case "lowercase":
        return acc.toLowerCase();
      case "zero_pad":
        return /^\d+$/.test(acc) ? acc.padStart(rule.maxLength, "0") : acc;
    }
  }, value);
}

export function formatIdentifier(value: string, rule: CompanyIdentifier): string {
  if (!rule.mask) return value;
  const chars = normalizeIdentifier(value, rule).split("");
  let out = "";
  for (const token of rule.mask) {
    if (chars.length === 0) break;
    out += token === "#" || token === "A" || token === "X" ? chars.shift() : token;
  }
  return out + chars.join("");
}

export function isValidIdentifier(value: string, rule: CompanyIdentifier): boolean {
  const normalized = normalizeIdentifier(value ?? "", rule);
  return (
    normalized.length >= rule.minLength &&
    normalized.length <= rule.maxLength &&
    new RegExp(rule.regex).test(normalized)
  );
}

export function isAcceptedIdentifier(value: string, rule: CompanyIdentifier): boolean {
  return rule.isFormatAdvisory
    ? normalizeIdentifier(value ?? "", rule) !== ""
    : isValidIdentifier(value, rule);
}

export function preselectEntityType(country: RegistryCountry | undefined): EntityType | null {
  const preselected = country?.entityTypes.find((entityType) => entityType.isDefault);
  return preselected && preselected.onboarding.allowed ? preselected : null;
}

export function requiresSubdivision(country: RegistryCountry | undefined): boolean {
  return primaryIdentifier(country)?.scope === "subdivision";
}

export function subdivisionOptions(
  country: RegistryCountry | undefined,
  lang?: Language,
): Subdivision[] {
  const options = country?.subdivisions ?? [];
  return lang
    ? [...options].sort((a, b) => a.label[lang].localeCompare(b.label[lang], lang))
    : options;
}

export function isValidSubdivision(value: string, country: RegistryCountry | undefined): boolean {
  return subdivisionOptions(country).some((option) => option.code === value);
}
